import { ApolloClient, ApolloProvider } from '@apollo/client'
import CloseIcon from '@mui/icons-material/Close'
import IconButton from '@mui/material/IconButton'
import SvgIcon from '@mui/material/SvgIcon'
import makeStyles from '@mui/styles/makeStyles'
import 'core-js/features/array/flat'
import checkoutSdkUrl from 'ipsp-js-sdk/dist/checkout.min.js?url'
import 'swiper/css'
import 'swiper/css/grid'
import 'swiper/css/lazy'
import 'swiper/css/navigation'
import 'swiper/css/pagination'
import 'swiper/css/free-mode'
import 'swiper/css/scrollbar'
import 'swiper/css/effect-fade'
import './i18n'
import { SnackbarProvider } from 'notistack'
import React, { useEffect, useState } from 'react'
import Cookies from 'js-cookie'

import { render } from 'react-dom'
import { ErrorBoundary, FallbackProps } from 'react-error-boundary'
import TagManager from 'react-gtm-module'
import { BrowserRouter as Router, Route } from 'react-router-dom'
import { GoogleReCaptchaProvider } from 'react-google-recaptcha-v3'
import SwiperCore, {
  Autoplay,
  FreeMode,
  Grid,
  Lazy,
  Navigation,
  Pagination,
  Thumbs,
  Mousewheel,
  EffectFade,
} from 'swiper'

import { QueryParamProvider, transformSearchStringJsonSafe } from 'use-query-params'
import createClient from './apolloClient'
import Routes from './Routes'

import ThemeProvider from './SPA/Theme'
import { GTM_ID, COOKIE_NAMES, SITE_RECAPTCHA_KEY, DEFAULT_LANGUAGE, SNACKBAR_CONFIG } from './constants'
import { RecentlyViewedProvider } from '@components/RecentlyViewed/RecentlyViewedProvider'
import { useTranslation } from 'react-i18next'
import { GlobalLoaderContext } from '@components/GlobalLoaderProvider/GlobalLoaderContext'

// Loaded as a classic (non-module) script rather than imported directly: the SDK relies on
// `arguments.callee`, which throws once the file is pulled into an ES module graph (ES modules
// are always strict mode). This attaches it to `window.$checkout`, same as its own UMD fallback.
const checkoutScript = document.createElement('script')
checkoutScript.src = checkoutSdkUrl
document.head.appendChild(checkoutScript)

SwiperCore.use([Thumbs, Navigation, Lazy, Pagination, Autoplay, Grid, FreeMode, Mousewheel, EffectFade])

TagManager.initialize({
  gtmId: GTM_ID,
})

const useStyles = makeStyles(theme => ({
  snackbar: {
    fontSize: '1.2rem',
    fontFamily: 'inherit',
    borderRadius: 0,
    padding: '4px 16px',
    '& #notistack-snackbar': {
      flex: 1,
    },
  },
  containerAnchorOriginTopRight: {
    top: 66,
  },
  snackbarError: {
    backgroundColor: '#820000 !important',
  },
  snackbarSuccess: {
    backgroundColor: '#006505 !important',
  },
  snackbarInfo: {
    backgroundColor: '#003e6f !important',
  },
  snackbarWarn: {
    backgroundColor: '#c37500 !important',
  },
}))

const stringifyOptions = {
  transformSearchString: transformSearchStringJsonSafe,
}

const ErrorFallback: React.FC<FallbackProps> = ({ error, componentStack, resetErrorBoundary }) => {
  const { t } = useTranslation()
  console.log('error boundary', error)
  // if (process.env.NODE_ENV !== 'development') {resetErrorBoundary();}
  return (
    <div role='alert'>
      <p>{t('somethingWentWrong', 'Something went wrong')}:</p>
      <pre>{error.message}</pre>
      <pre>{componentStack}</pre>
      <button onClick={resetErrorBoundary}>{t('button.tryAgain', 'Try again')}</button>
    </div>
  )
}

const App: React.FC<{ client: ApolloClient<any> }> = ({ client }) => {
  const { i18n } = useTranslation()
  const classes = useStyles()
  const [isLoading, setIsLoading] = useState(false)

  useEffect(() => {
    Cookies.set(COOKIE_NAMES.LANGUAGE, i18n.language || DEFAULT_LANGUAGE, { sameSite: 'lax' })
    //   setIsLoading(true)
    //   client.resetStore().finally(() => setIsLoading(false))
  }, [i18n.language])

  useEffect(() => {
    i18n.on('languageChanged', () => {
      Cookies.set(COOKIE_NAMES.LANGUAGE, i18n.language || DEFAULT_LANGUAGE, { sameSite: 'lax' })
      setIsLoading(true)
      client.resetStore().finally(() => setIsLoading(false))
    })
  }, [i18n])

  const errorHandler = (error, componentStack) => {
    console.log('error handler', error, componentStack)
  }

  const notistackRef = React.createRef<any>()
  const onClickDismiss = key => () => {
    notistackRef.current.closeSnackbar(key)
  }

  return (
    <ApolloProvider client={client}>
      <GoogleReCaptchaProvider
        reCaptchaKey={SITE_RECAPTCHA_KEY}
        language={i18n.language}
        useEnterprise={true}
        scriptProps={{
          async: true,
          defer: false,
          appendTo: 'head',
          nonce: undefined,
        }}
      >
        <Router>
          <ErrorBoundary
            FallbackComponent={ErrorFallback}
            onError={errorHandler}
            // onReset={() => {
            //     apolloClient.resetStore()
            // }}
          >
            <ThemeProvider isDefaultDark={false}>
              <SnackbarProvider
                maxSnack={SNACKBAR_CONFIG.MAX_SNACK}
                autoHideDuration={SNACKBAR_CONFIG.SNACKBAR_AUTO_HIDE_DURATION}
                classes={{
                  variantSuccess: `${classes.snackbar} ${classes.snackbarSuccess}`,
                  variantError: `${classes.snackbar} ${classes.snackbarError}`,
                  variantInfo: `${classes.snackbar} ${classes.snackbarInfo}`,
                  variantWarning: `${classes.snackbar} ${classes.snackbarWarn}`,
                  containerAnchorOriginTopRight: classes.containerAnchorOriginTopRight,
                }}
                ref={notistackRef}
                action={key => (
                  <IconButton onClick={onClickDismiss(key)} color='inherit'>
                    <SvgIcon fontSize='medium'>
                      <CloseIcon color='inherit' />
                    </SvgIcon>
                  </IconButton>
                )}
              >
                <GlobalLoaderContext.Provider
                  value={{
                    isActiveLoader: isLoading,
                    setLoader: setIsLoading,
                  }}
                >
                  <QueryParamProvider ReactRouterRoute={Route} stringifyOptions={stringifyOptions}>
                    <RecentlyViewedProvider>
                      <Routes />
                    </RecentlyViewedProvider>
                  </QueryParamProvider>
                </GlobalLoaderContext.Provider>
              </SnackbarProvider>
            </ThemeProvider>
          </ErrorBoundary>
        </Router>
      </GoogleReCaptchaProvider>
    </ApolloProvider>
  )
}

const startApp = () => {
  // if (process.env.NODE_ENV !== 'development') {
  //   Sentry.init({
  //     dsn: "https://3dd9927d1ae948ee8108a5e586662227@o447771.ingest.sentry.io/5428074",
  //     integrations: [
  //       new Integrations.BrowserTracing(),
  //     ],
  //     tracesSampleRate: 1.0,
  //   });
  // }
  createClient().then(client => {
    let root = null
    while (!root) {
      root = document.getElementById('root')
    }
    render(<App client={client} />, root)
  })
}
startApp()
